import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import moment from 'moment'
import classNames from 'classnames'

import ButtonDropdown, {
  DropdownButtonItem,
  DropdownButtonItemProps,
} from './button-dropdown'
import { sendFeatureRequest } from '../api/apollo/graphql/support-client'
import { getCampaignLinkRawData } from '../api/apollo/REST/report-client'
import { SelectedDimensions } from '../api/types'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/acquisition-actions.module.scss'

export interface AcquisitionActionsProps {
  selectedDimensions: SelectedDimensions
  currentDate: string
  startDate: Date
  endDate: Date
  granularity: string
  matchType: string
  selectedMetric: string
  stackDimension: string
  reportTitle?: string
  className?: string
  onEditMetric?: () => void
  children: React.ReactElement<DropdownButtonItemProps>[]
}

export default function AcquisitionActions({
  selectedDimensions,
  startDate,
  currentDate,
  endDate,
  granularity,
  matchType = 'full',
  selectedMetric,
  stackDimension,
  reportTitle = 'Link_Performance_Export',
  className,
  onEditMetric,
  children,
}: AcquisitionActionsProps): React.ReactElement {
  const logAction = useLogAction()

  const [requestFeature] = useMutation(sendFeatureRequest)

  const [exporting, setExporting] = useState(false)
  const [isActive, setIsActive] = useState(false)

  return (
    <ButtonDropdown
      buttonText="Actions"
      containerClassName={classNames(className, styles.bulkButtons)}
      separateMainAction={false}
      mainAction={() => {
        if (!isActive) {
          logAction({
            variables: {
              action: 'action-drop-down-clicked',
              extra: '',
              websiteSection: 'report',
              functionName: 'clickDropdown',
              pagePath: window.location.pathname,
            },
          })
        }

        setIsActive(!isActive)
      }}
    >
      {[
        ...(Array.isArray(children) ? children : [children]),
        <DropdownButtonItem
          key="createInsights"
          className={styles.softDisable}
          onPress={async () => {
            const message = `Perfomance dashboard "Create insight" feature request`
            await requestFeature({
              variables: {
                page: 'report/performance',
                message,
              },
            })
          }}
        >
          Create insight
        </DropdownButtonItem>,
        <DropdownButtonItem key="downloadPDF" isDisabled>
          Download as PDF
        </DropdownButtonItem>,
        <DropdownButtonItem
          key="downloadExcel"
          loading={exporting}
          onPress={async (e) => {
            setExporting(true)

            const data = Object.keys(selectedDimensions).map(
              (dimensionName) => {
                return {
                  dimensionParameterID:
                    selectedDimensions[dimensionName].dimensionParameterID,
                  dimensionName,
                  dimensionOptions: [
                    ...selectedDimensions[dimensionName].dimensionOptions,
                  ],
                }
              },
            )

            const from =
              currentDate === 'custom'
                ? moment(startDate).format('YYYY-MM-DD')
                : currentDate

            const to =
              currentDate === 'custom'
                ? moment(endDate).format('YYYY-MM-DD')
                : currentDate

            const rawExcelData = {
              startDate: from,
              endDate: to,
              granularity,
              selectedMetric,
              stackDimension,
              matchType,
              applyFilters: [...data],
            }

            await getCampaignLinkRawData(rawExcelData, reportTitle)

            logAction({
              variables: {
                action: 'download-data-excel',
                extra: JSON.stringify(rawExcelData),
                websiteSection: 'report',
                functionName: 'getCampaignLinkRawData',
                pagePath: window.location.pathname,
              },
            })

            setExporting(false)
          }}
        >
          Download to Excel
        </DropdownButtonItem>,
        <DropdownButtonItem
          key="share"
          className={styles.softDisable}
          onPress={async () => {
            const message = `Perfomance dashboard "Share" feature request`
            await requestFeature({
              variables: {
                page: 'report/performance',
                message,
              },
            })
          }}
        >
          Share
        </DropdownButtonItem>,
        <React.Fragment key="editMetrics">
          {onEditMetric && (
            <DropdownButtonItem onPress={() => onEditMetric()}>
              Edit metrics
            </DropdownButtonItem>
          )}
        </React.Fragment>,
      ]}
    </ButtonDropdown>
  )
}
